import { useEffect, useState } from "react";
import { useForm } from "../useForm";

import { Dialog } from "@headlessui/react";

import Input from "../../controls/Input";
import SelectMenu from "../../controls/SelectMenu";
import TextArea from "../../controls/TextArea";
import Button from "../../controls/Button";

const initialFValues = {
  id: 0,
  order_id: 0,
  labour_id: { value: -1, label: "" },
  name: "",
  unit_price: "",
  amount: "",
  total_price: "",
  employee_assigned: { value: -1, label: "" },
  description: "",
};

export default function ManualLabourAssignmentModal(props) {
  const {
    item,
    isEdit,
    setOpen,
    actionCall,
    title,
    manualLabourOptions,
    orderId,
    employeeOptions,
  } = props;

  const [submitting, setSubmitting] = useState(false);

  const { values, setValues, errors, handleInputChange } = useForm(
    initialFValues,
    false,
  );

  useEffect(() => {
    if (item) {
      setValues({
        ...item,
        labour_id: item.manual_labour
          ? {
              value: item.labour_id,
              label: item.manual_labour.labour_type,
            }
          : { value: -1, label: "" },
      });
    } else {
      setValues({ ...initialFValues, order_id: orderId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item, orderId]);

  // Auto-calculate total cost when hourly wage or hours worked changes
  useEffect(() => {
    if (values.unit_price && values.amount) {
      const wage = parseFloat(values.unit_price);
      const hours = parseFloat(values.amount);
      if (!isNaN(wage) && !isNaN(hours)) {
        const calculatedCost = (wage * hours).toFixed(2);
        setValues((prev) => ({ ...prev, total_price: calculatedCost }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.unit_price, values.amount]);

  const handleSubmit = async () => {
    if (submitting) {
      return;
    }

    const payload = {
      ...values,
      labour_id: values.labour_id.value,
      employee_id: values.employee_assigned.value,
      order_id: orderId,
    };

    setSubmitting(true);
    try {
      await actionCall(payload);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="sm:flex sm:items-start">
      <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
        <Dialog.Title
          as="h3"
          className="text-base font-semibold leading-6 text-gray-900"
        >
          {title}
        </Dialog.Title>

        <div className="mt-4 space-y-4">
          <div>
            <SelectMenu
              handleInputChange={handleInputChange}
              name="labour_id"
              options={manualLabourOptions}
              value={values.labour_id}
              title={"Labour Type *"}
              error={errors.labour_id}
              showLabel={true}
            />
          </div>

          <div>
            <SelectMenu
              handleInputChange={handleInputChange}
              name="employee_assigned"
              options={employeeOptions}
              value={values.employee_assigned}
              title={"Employee Name"}
              error={errors.employee_assigned}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input
              label="Hourly Wage"
              name="unit_price"
              onChange={handleInputChange}
              value={values.unit_price}
              labelOn={true}
              type="number"
              step="0.01"
              error={errors.unit_price}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
            />

            <Input
              label="Hours Worked"
              name="amount"
              onChange={handleInputChange}
              value={values.amount}
              labelOn={true}
              type="number"
              step="0.01"
              error={errors.amount}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
            />
          </div>

          <div>
            <Input
              label="Total Cost"
              name="total_price"
              onChange={handleInputChange}
              value={values.total_price}
              labelOn={true}
              type="number"
              step="0.01"
              error={errors.total_price}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
            />
          </div>

          <div>
            <TextArea
              label="Description"
              name="description"
              onChange={handleInputChange}
              value={values.description}
              labelOn={true}
              error={errors.description}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
            />
          </div>
        </div>

        <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
          <button
            type="button"
            disabled={submitting}
            className="inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0 disabled:cursor-not-allowed disabled:opacity-50"
            onClick={() => {
              setOpen({
                childComponent: null,
                open: false,
              });
            }}
          >
            Cancel
          </button>
          <Button
            type="submit"
            onClick={handleSubmit}
            loading={submitting}
            className="w-full sm:col-start-2"
          >
            {isEdit ? "Update" : "Create"}
          </Button>
        </div>
      </div>
    </div>
  );
}
